// src/pages/Aanvragen.js
// vAAN-011
// - Smartphone: na selectie van een regel wordt de invoerkolom bovenaan in beeld gezet
// - Verkooptarief wordt automatisch berekend uit Factor x Salarisindicatie, maar blijft handmatig aanpasbaar
//
// StaffIQ HRM - Aanvragen
// Opzet gelijk aan Personeel / Matching:
// - Links/boven: zoeken + knoppen
// - Midden: overzicht met sortering en kolomfilters
// - Rechts: detailkaart

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const VERSION = "vAAN-011";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return 0;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    // Nederlandse invoer: 2,35 of 2.350,75
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // Engelse/database-notatie: 2.35 moet 2.35 blijven.
    // Alleen meerdere punten zien we als duizendtallen: 2.350.750 -> 2350750
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function decimalInput(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";

  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function cleanDecimalInputValue(v) {
  return String(v || "").replace(/[^0-9,.-]/g, "");
}

function formatDecimalInputValue(v, decimals = 2) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  const n = parseDecimal(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

function getSelectedCompanyCodeIdsHeader() {
  return (
    sessionStorage.getItem("selectedCompanyCodeIds") ||
    localStorage.getItem("selectedCompanyCodeIds") ||
    sessionStorage.getItem("company_code_ids") ||
    localStorage.getItem("company_code_ids") ||
    ""
  );
}

function companyCodeDisplayName(row = {}) {
  return safeTrim(row.company_code_name || row.company_code || row.company_code_display || "");
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-company-code-ids": getSelectedCompanyCodeIdsHeader() || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function emptyFormToday() {
  return {
    id: null,
    application_date: new Date().toISOString().slice(0, 10),
    status: "Open",
    application_number: "",
    organisation_id: "",
    contact_id: "",
    company_code_id: "",
    company_code: "",
    company_code_name: "",
    title: "",
    job_title: "",
    number_of_people: "",
    work_description: "",
    start_date: "",
    end_date: "",
    hours_per_week: "",
    schedule_text: "",
    location: "",
    place: "",
    region: "",
    education: "",
    experience: "",
    certificates: "",
    driving_license: "",
    own_transport: "",
    languages: "",
    matching_text: "",
    cao: "",
    wage_period: "week",
    invoice_period: "week",
    factor: "",
    rate_indication: "",
    sales_rate: "",
    min_rate: "",
    travel_costs: "",
    allowances: "",
    invoice_agreement: "",
    priority: "Normaal",
    request_source: "",
    proposal_deadline: "",
    internal_note: "",
    external_note: "",
    remarks: "",
    active: true,
  };
}

function pickOrgName(o) {
  return safeTrim(o?.search_name || o?.zoeknaam || o?.name || o?.naam || o?.bedrijfsnaam || "");
}

function orgIdOf(o) {
  return o?.id ?? o?.organisation_id ?? o?.organisatie_id ?? "";
}

function orgNameFromList(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return pickOrgName(found) || fallback || "";
}

function pickContactName(c) {
  const fn = safeTrim(c?.first_name || c?.voornaam || "");
  const ln = safeTrim(c?.last_name || c?.achternaam || "");
  const full = safeTrim(c?.full_name || c?.naam || "");
  return safeTrim(c?.search_name || c?.display_name || (ln && fn ? `${ln}, ${fn}` : "") || full || fn || "");
}

function contactIdOf(c) {
  return c?.id ?? c?.contact_id ?? "";
}

function orgCompanyCodeIdFromList(list, id) {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return found?.company_code_id ?? "";
}

function orgCompanyCodeNameFromList(list, id) {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return companyCodeDisplayName(found);
}

function applicationCompanyCodeName(item = {}, organisationsList = []) {
  return (
    companyCodeDisplayName(item) ||
    orgCompanyCodeNameFromList(organisationsList, item?.organisation_id) ||
    ""
  );
}

function normalizeApplication(raw = {}) {
  return {
    ...raw,
    id: raw.id ?? null,
    application_date: isoDate(raw.application_date),
    start_date: isoDate(raw.start_date),
    end_date: isoDate(raw.end_date),
    proposal_deadline: isoDate(raw.proposal_deadline),
    organisation_id: raw.organisation_id ?? "",
    contact_id: raw.contact_id ?? "",
    company_code_id: raw.company_code_id ?? "",
    company_code: raw.company_code ?? "",
    company_code_name: raw.company_code_name ?? "",
    number_of_people: raw.number_of_people ?? "",
    hours_per_week: decimalInput(raw.hours_per_week, 2),
    factor: decimalInput(raw.factor, 2),
    rate_indication: decimalInput(raw.rate_indication, 2),
    sales_rate: decimalInput(raw.sales_rate, 2),
    min_rate: decimalInput(raw.min_rate ?? raw.max_rate, 2),
    travel_costs: decimalInput(raw.travel_costs, 2),
    active: parseActiveValue(raw) !== false,
  };
}

export default function Aanvragen() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyFormToday());
  const [listIndex, setListIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");
  const [q, setQ] = useState(getParam("q"));

  const EMPTY_VAL = "__EMPTY__";
  const [dateFilter, setDateFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [caoFilter, setCaoFilter] = useState("");
  const [wagePeriodFilter, setWagePeriodFilter] = useState("");
  const [invoicePeriodFilter, setInvoicePeriodFilter] = useState("");
  const [factorFilter, setFactorFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

  const [mode, setMode] = useState("search");
  const isFormLocked = isReadonlyRole || mode !== "form";
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const rightScrollRef = useRef(null);

  const dateRef = useRef(null);
  const numberRef = useRef(null);
  const relationRef = useRef(null);
  const contactRef = useRef(null);
  const titleRef = useRef(null);
  const functionRef = useRef(null);
  const statusRef = useRef(null);
  const peopleRef = useRef(null);
  const workDescriptionRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const hoursRef = useRef(null);
  const scheduleRef = useRef(null);
  const locationRef = useRef(null);
  const placeRef = useRef(null);
  const regionRef = useRef(null);
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const certificatesRef = useRef(null);
  const drivingLicenseRef = useRef(null);
  const ownTransportRef = useRef(null);
  const languagesRef = useRef(null);
  const matchingTextRef = useRef(null);
  const caoRef = useRef(null);
  const wagePeriodRef = useRef(null);
  const invoicePeriodRef = useRef(null);
  const factorRef = useRef(null);
  const rateIndicationRef = useRef(null);
  const salesRateRef = useRef(null);
  const minRateRef = useRef(null);
  const travelCostsRef = useRef(null);
  const allowancesRef = useRef(null);
  const invoiceAgreementRef = useRef(null);
  const priorityRef = useRef(null);
  const sourceRef = useRef(null);
  const proposalDeadlineRef = useRef(null);
  const internalNoteRef = useRef(null);
  const externalNoteRef = useRef(null);

  const focusOrderRefs = [
    dateRef,
    numberRef,
    relationRef,
    contactRef,
    titleRef,
    functionRef,
    statusRef,
    peopleRef,
    workDescriptionRef,
    startDateRef,
    endDateRef,
    hoursRef,
    scheduleRef,
    locationRef,
    placeRef,
    regionRef,
    educationRef,
    experienceRef,
    certificatesRef,
    drivingLicenseRef,
    ownTransportRef,
    languagesRef,
    matchingTextRef,
    caoRef,
    wagePeriodRef,
    invoicePeriodRef,
    factorRef,
    rateIndicationRef,
    salesRateRef,
    minRateRef,
    travelCostsRef,
    allowancesRef,
    invoiceAgreementRef,
    priorityRef,
    sourceRef,
    proposalDeadlineRef,
    internalNoteRef,
    externalNoteRef,
  ];

  async function loadOrganisations() {
    try {
      const data = await tryFetchJson(["/api/organisations"]);
      setOrganisations(Array.isArray(data) ? data : []);
    } catch {
      setOrganisations([]);
    }
  }

  async function loadContacts(organisationId) {
    const id = safeTrim(organisationId);
    if (!id) {
      setContacts([]);
      return;
    }

    try {
      const data = await tryFetchJson([`/api/contacts?organisation_id=${encodeURIComponent(id)}`]);
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    }
  }

  async function loadList(keepSelection = true) {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (safeTrim(q)) params.set("q", safeTrim(q));
      const data = await tryFetchJson([`/api/applications?${params.toString()}`, `/api/aanvragen?${params.toString()}`]);
      const rows = Array.isArray(data) ? data.map(normalizeApplication) : [];

      setItems(rows);

      if (keepSelection && selectedId) {
        const idx = rows.findIndex((x) => String(x.id) === String(selectedId));
        if (idx >= 0) {
          setListIndex(idx);
          return rows;
        }
      }

      setListIndex(rows.length ? 0 : -1);
      return rows;
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
      setListIndex(-1);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrganisations();
  }, []);

  useEffect(() => {
    loadContacts(form.organisation_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.organisation_id]);

  useEffect(() => {
    loadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function relationName(item) {
    return orgNameFromList(organisations, item?.organisation_id, item?.organisation_name || "");
  }

  function companyCodeName(item) {
    return applicationCompanyCodeName(item, organisations);
  }

  function rowText(item) {
    return [
      item?.application_date,
      item?.application_number,
      relationName(item),
      item?.title,
      item?.job_title,
      item?.status,
      item?.number_of_people,
      item?.work_description,
      item?.start_date,
      item?.end_date,
      item?.hours_per_week,
      item?.schedule_text,
      item?.location,
      item?.place,
      item?.region,
      item?.education,
      item?.experience,
      item?.certificates,
      item?.driving_license,
      item?.own_transport,
      item?.languages,
      item?.matching_text,
      item?.cao,
      item?.wage_period,
      item?.invoice_period,
      item?.factor,
      item?.rate_indication,
      item?.sales_rate,
      item?.min_rate ?? item?.max_rate,
      item?.travel_costs,
      item?.allowances,
      item?.invoice_agreement,
      item?.priority,
      item?.request_source,
      item?.proposal_deadline,
      item?.internal_note,
      item?.external_note,
      item?.remarks,
      companyCodeName(item),
      boolToJaNee(parseActiveValue(item) !== false),
    ].join(" ");
  }

  function filterBySelect(value, filter) {
    if (!filter) return true;
    const v = safeTrim(value);
    if (filter === EMPTY_VAL) return !v;
    return norm(v) === norm(filter);
  }

  const filtered = useMemo(() => {
    const query = norm(q);

    return (items || []).filter((item) => {
      if (query && !norm(rowText(item)).includes(query)) return false;
      if (!filterBySelect(formatDateNL(item.application_date), dateFilter)) return false;
      if (!filterBySelect(relationName(item), relationFilter)) return false;
      if (!filterBySelect(item.title, titleFilter)) return false;
      if (!filterBySelect(item.job_title, functionFilter)) return false;
      if (!filterBySelect(item.status, statusFilter)) return false;
      if (!filterBySelect(item.cao, caoFilter)) return false;
      if (!filterBySelect(item.wage_period, wagePeriodFilter)) return false;
      if (!filterBySelect(item.invoice_period, invoicePeriodFilter)) return false;
      if (!filterBySelect(decimalInput(item.factor), factorFilter)) return false;
      if (!filterBySelect(companyCodeName(item), companyCodeFilter)) return false;

      const activeBool = parseActiveValue(item) !== false;
      if (activeFilter === "j" && !activeBool) return false;
      if (activeFilter === "n" && activeBool) return false;

      return true;
    });
  }, [items, q, dateFilter, relationFilter, titleFilter, functionFilter, statusFilter, caoFilter, wagePeriodFilter, invoicePeriodFilter, factorFilter, companyCodeFilter, activeFilter, organisations]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    const dir = sort.dir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      function val(x) {
        if (sort.key === "date") return x.application_date || "";
        if (sort.key === "relation") return relationName(x);
        if (sort.key === "title") return x.title || "";
        if (sort.key === "function") return x.job_title || "";
        if (sort.key === "status") return x.status || "";
        if (sort.key === "cao") return x.cao || "";
        if (sort.key === "wage") return x.wage_period || "";
        if (sort.key === "invoice") return x.invoice_period || "";
        if (sort.key === "factor") return Number(String(x.factor || "").replace(",", ".")) || 0;
        if (sort.key === "active") return parseActiveValue(x) !== false ? "j" : "n";
        if (sort.key === "companyCode") return companyCodeName(x);
        return "";
      }

      const av = val(a);
      const bv = val(b);

      if (typeof av === "number" || typeof bv === "number") {
        return ((av || 0) - (bv || 0)) * dir;
      }

      return String(av).localeCompare(String(bv), "nl", { numeric: true }) * dir;
    });

    return arr;
  }, [filtered, sort, organisations]);

  const gridCount = sorted.length > 9999 ? "9999+" : sorted.length;

  const dateOptions = uniqSorted(items.map((x) => formatDateNL(x.application_date)));
  const relationOptions = uniqSorted(items.map((x) => relationName(x)));
  const titleOptions = uniqSorted(items.map((x) => x.title));
  const functionOptions = uniqSorted(items.map((x) => x.job_title));
  const statusOptions = uniqSorted(items.map((x) => x.status));
  const caoOptions = uniqSorted(items.map((x) => x.cao));
  const wageOptions = uniqSorted(items.map((x) => x.wage_period));
  const invoiceOptions = uniqSorted(items.map((x) => x.invoice_period));
  const factorOptions = uniqSorted(items.map((x) => decimalInput(x.factor)));
  const companyCodeOptions = uniqSorted(items.map((x) => companyCodeName(x)));

  async function loadItem(row) {
    if (!row?.id) return;

    try {
      setSaveError(null);
      const data = await tryFetchJson([`/api/applications/${row.id}`, `/api/aanvragen/${row.id}`]);
      const item = normalizeApplication(data);
      setSelectedId(item.id);
      setForm({ ...emptyFormToday(), ...item });
      setMode("form");

      setTimeout(() => {
        try {
          const panel = rightScrollRef.current;
          if (!panel) return;

          // Altijd eerst de invoerkolom intern naar de eerste rubriek.
          panel.scrollTop = 0;

          if (window.innerWidth <= 768) {
            // Smartphone: zet de volledige invoerkolom bovenaan in het scherm.
            // Geen automatische focus, zodat het toetsenbord dicht blijft.
            panel.scrollIntoView?.({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });

            if (
              document.activeElement &&
              typeof document.activeElement.blur === "function"
            ) {
              document.activeElement.blur();
            }

            return;
          }

          // Desktop: cursor op de eerste rubriek.
          dateRef.current?.focus?.();
        } catch {}
      }, 120);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function newItem() {
    if (isReadonlyRole) return;
    setSelectedId(null);
    setForm(emptyFormToday());
    setMode("form");
    setSaveError(null);
    requestAnimationFrame(() => {
      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        dateRef.current?.focus?.();
      } catch {}
    });
  }

  function vernieuwList() {
    setMode("search");
    setSelectedId(null);
    setForm(emptyFormToday());
    setSaveError(null);
    loadList(false);
  }

  function openMatchingPersoneelFromApplication() {
    const applicationId = form.id || selectedId;

    if (!applicationId) {
      alert("Sla de aanvraag eerst op voordat je personeel gaat matchen.");
      return;
    }

    const params = new URLSearchParams();
    params.set("application_id", String(applicationId));

    if (safeTrim(form.job_title || form.title)) params.set("functie", safeTrim(form.job_title || form.title));
    if (safeTrim(form.region)) params.set("regio", safeTrim(form.region));
    if (safeTrim(form.place)) params.set("plaats", safeTrim(form.place));
    if (safeTrim(form.driving_license)) params.set("rijbewijs", safeTrim(form.driving_license));
    if (safeTrim(form.experience)) params.set("ervaring", safeTrim(form.experience));
    if (safeTrim(form.own_transport)) params.set("eigenVervoer", safeTrim(form.own_transport));
    if (safeTrim(form.matching_text)) params.set("matchingGegevens", safeTrim(form.matching_text));

    const searchText = safeTrim(form.matching_text || form.job_title || form.title || "");
    if (searchText) params.set("q", searchText);

    window.location.href = `/matching-personeel?${params.toString()}`;
  }

  async function saveItem() {
    if (isReadonlyRole) return;

    const payload = {
      ...form,
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      company_code_id: form.company_code_id ? Number(form.company_code_id) : null,
      number_of_people: form.number_of_people ? Number(form.number_of_people) : null,
      hours_per_week: form.hours_per_week === "" ? null : parseDecimal(form.hours_per_week),
      factor: form.factor === "" ? null : parseDecimal(form.factor),
      rate_indication: form.rate_indication === "" ? null : parseDecimal(form.rate_indication),
      sales_rate: form.sales_rate === "" ? null : parseDecimal(form.sales_rate),
      min_rate: form.min_rate === "" ? null : parseDecimal(form.min_rate),
      travel_costs: form.travel_costs === "" ? null : parseDecimal(form.travel_costs),
      active: form.active !== false,
    };

    try {
      setSaveError(null);

      const saved = await tryFetchJson(
        form.id
          ? [`/api/applications/${form.id}`, `/api/aanvragen/${form.id}`]
          : ["/api/applications", "/api/aanvragen"],
        {
          method: form.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const normalized = normalizeApplication(saved);
      setSelectedId(normalized.id);
      setForm({ ...emptyFormToday(), ...normalized });
      setItems((prev) => {
        const idx = prev.findIndex((x) => String(x.id) === String(normalized.id));
        if (idx === -1) return [normalized, ...prev];
        const copy = prev.slice();
        copy[idx] = normalized;
        return copy;
      });

      setMode("search");
      setFlash("Aanvraag opgeslagen ✅");
      setTimeout(() => setFlash(""), 1000);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonlyRole || !form.id) return;
    if (!window.confirm("Aanvraag verwijderen?")) return;

    try {
      await tryFetchJson([`/api/applications/${form.id}`, `/api/aanvragen/${form.id}`], { method: "DELETE" });
      setItems((prev) => prev.filter((x) => String(x.id) !== String(form.id)));
      setSelectedId(null);
      setForm(emptyFormToday());
      setMode("search");
      setFlash("Aanvraag verwijderd ✅");
      setTimeout(() => setFlash(""), 1000);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function moveCursor(delta) {
    if (!sorted.length) return;
    let idx = listIndex;
    if (idx < 0) idx = 0;
    idx = Math.max(0, Math.min(sorted.length - 1, idx + delta));
    setListIndex(idx);

    requestAnimationFrame(() => {
      try {
        rowRefs.current[idx]?.scrollIntoView?.({ block: "nearest" });
      } catch {}
    });
  }

  function openCurrentRow() {
    const idx = listIndex >= 0 ? listIndex : 0;
    const row = sorted[idx];
    if (row) loadItem(row);
  }

  function getFocusableElements() {
    return focusOrderRefs
      .map((r) => r?.current)
      .filter(Boolean)
      .filter((el) => {
        try {
          if (el.disabled) return false;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        } catch {
          return false;
        }
      });
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : 0;

    const next = els[Math.max(0, Math.min(els.length - 1, idx + dir))];
    try {
      next?.focus?.();
      next?.select?.();
    } catch {}
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;

      if (cmd.type === "next") {
        if (mode === "form") focusNextField(+1);
        else moveCursor(+1);
        return true;
      }

      if (cmd.type === "prev") {
        if (mode === "form") focusNextField(-1);
        else moveCursor(-1);
        return true;
      }

      if (cmd.type === "new") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;
      if (cmd.type === "select") return openCurrentRow(), true;

      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return mode === "form" ? focusNextField(+1) : moveCursor(+1);
      if (t === "vorige" || t === "terug") return mode === "form" ? focusNextField(-1) : moveCursor(-1);
      if (t === "nieuw") return newItem();
      if (t === "vernieuw" || t === "vernieuwen") return vernieuwList();
      if (t === "opslaan") return saveItem();
      if (t === "selecteer" || t === "item" || t === "rechts") return openCurrentRow();
      if (t === "lijst" || t === "links") return setMode("search");

      if (mode === "search") {
        if (isEmptyWord(raw)) setQ("");
        else setQ(raw);
      }
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1280;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

  const colWidths = {
    date: 90,
    relation: 150,
    title: 125,
    function: 115,
    status: 75,
    cao: 75,
    wage: 90,
    invoice: 100,
    factor: 65,
    active: 55,
    companyCode: 115,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";
  const labelStyle = { fontSize: 13, color: "#333" };
  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x || EMPTY_VAL} value={x || EMPTY_VAL}>
              {x || "(leeg)"}
            </option>
          ))
        )}
      </select>
    );
  }

  function sortToggle(key) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === "date" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function renderTextField(label, key, ref = null, type = "text", extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            if (extra.onChange) {
              extra.onChange(e.target.value);
              return;
            }
            setForm({ ...form, [key]: e.target.value });
          }}
          style={inputStyle}
          placeholder={extra.placeholder || ""}
          list={extra.list || undefined}
        />
      </>
    );
  }

  function formatDecimalNumberValue(n, decimals = 2) {
    if (!Number.isFinite(n)) return "";

    return n.toLocaleString("nl-NL", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  function applyCalculatedSalesRate(nextForm) {
    const factorRaw = String(nextForm.factor || "").trim();
    const rateRaw = String(nextForm.rate_indication || "").trim();

    // Als één van beide velden ontbreekt, laten we Verkooptarief gewoon vrij.
    if (!factorRaw || !rateRaw) return nextForm;

    const factorValue = parseDecimal(factorRaw);
    const rateValue = parseDecimal(rateRaw);

    if (!Number.isFinite(factorValue) || !Number.isFinite(rateValue)) return nextForm;

    return {
      ...nextForm,
      sales_rate: formatDecimalNumberValue(factorValue * rateValue, 2),
    };
  }

  function handleMoneyBlur(key, decimals = 2, extra = {}) {
    setForm((prev) => {
      const raw = String(prev[key] || "").trim();
      let next = { ...prev };

      if (raw) {
        const n = parseDecimal(raw);
        if (Number.isFinite(n)) {
          next[key] = formatDecimalInputValue(prev[key], decimals);
        }
      }

      if (extra.autoCalculateSalesRate === true) {
        next = applyCalculatedSalesRate(next);
      }

      return next;
    });
  }

  function renderMoneyField(label, key, ref = null, decimals = 2, extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            const clean = cleanDecimalInputValue(e.target.value);
            setForm({ ...form, [key]: clean });
          }}
          onBlur={() => handleMoneyBlur(key, decimals, extra)}
          style={inputStyle}
          placeholder="0,00"
        />
      </>
    );
  }

  function renderDecimalField(label, key, ref = null, decimals = 2, extra = {}) {
    return renderMoneyField(label, key, ref, decimals, extra);
  }

  function renderSelectField(label, key, ref, options) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          style={selectStyle}
        >
          <option value="">-</option>
          {options.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>
      </>
    );
  }

  function renderTextareaField(label, key, ref = null, minHeight = 70) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <textarea
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          style={{ ...inputStyle, minHeight, resize: "vertical" }}
        />
      </>
    );
  }

  function renderCheckbox(label, key) {
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        />
        {label}
      </label>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 ref={pageTopRef} style={{ marginTop: 0, marginBottom: 10 }}>
            Aanvragen <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

          {!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setMode("search");
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuwList} style={buttonStyle}>
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                  }}
                  placeholder="Zoek..."
                  style={{ ...searchInputStyle, width: "100%", minWidth: 0 }}
                />
                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                <button onClick={() => voice.startOnce?.()} disabled={!voice.supported || voice.listening} style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}>
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>
                <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>➕ Nieuw</button>
                <button onClick={vernieuwList} style={buttonStyle}>🔄 Vernieuw</button>
              </div>
            </>
          )}

          {speechInfo && <div style={{ marginTop: 8, fontSize: 13, color: "#1f4e79" }}>{speechInfo}</div>}
          {flash && <div style={{ marginTop: 8, fontSize: 13, color: "#1f7a1f" }}>{flash}</div>}
          {error && <div style={{ marginTop: 8, color: "#b00020" }}>{error}</div>}

          <div
            style={{
              marginTop: 12,
              border: "1px solid #e5e5e5",
              borderRadius: 6,
              background: "white",
              overflow: "auto",
              maxHeight: isMobile ? "45vh" : `calc(100dvh - ${MID_BOTTOM_OFFSET}px)`,
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: colWidths.date }} />
                <col style={{ width: colWidths.relation }} />
                <col style={{ width: colWidths.title }} />
                <col style={{ width: colWidths.function }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.cao }} />
                <col style={{ width: colWidths.wage }} />
                <col style={{ width: colWidths.invoice }} />
                <col style={{ width: colWidths.factor }} />
                <col style={{ width: colWidths.active }} />
                <col style={{ width: colWidths.companyCode }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thClickable} onClick={() => sortToggle("date")}>Aanvraagdatum{sortArrow("date")}</th>
                  <th style={thClickable} onClick={() => sortToggle("relation")}>Relatie{sortArrow("relation")}</th>
                  <th style={thClickable} onClick={() => sortToggle("title")}>Aanvraag{sortArrow("title")}</th>
                  <th style={thClickable} onClick={() => sortToggle("function")}>Functie{sortArrow("function")}</th>
                  <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                  <th style={thClickable} onClick={() => sortToggle("cao")}>CAO{sortArrow("cao")}</th>
                  <th style={thClickable} onClick={() => sortToggle("wage")}>Loonperiode{sortArrow("wage")}</th>
                  <th style={thClickable} onClick={() => sortToggle("invoice")}>Factuurperiode{sortArrow("invoice")}</th>
                  <th style={thClickable} onClick={() => sortToggle("factor")}>Factor{sortArrow("factor")}</th>
                  <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                  <th style={thClickable} onClick={() => sortToggle("companyCode")}>Bedrijfscode{sortArrow("companyCode")}</th>
                </tr>
                <tr>
                  <th style={filterThStyle}>{filterSelect(dateFilter, setDateFilter, dateOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(relationFilter, setRelationFilter, relationOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(titleFilter, setTitleFilter, titleOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(functionFilter, setFunctionFilter, functionOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(statusFilter, setStatusFilter, statusOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(caoFilter, setCaoFilter, caoOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(wagePeriodFilter, setWagePeriodFilter, wageOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(invoicePeriodFilter, setInvoicePeriodFilter, invoiceOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(factorFilter, setFactorFilter, factorOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                  <th style={filterThStyle}>{filterSelect(companyCodeFilter, setCompanyCodeFilter, companyCodeOptions)}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} style={tdStyle}>Laden...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={11} style={tdStyle}>Geen aanvragen gevonden.</td></tr>
                ) : (
                  sorted.map((item, idx) => {
                    const isSelected = String(item.id) === String(selectedId);
                    const isCursor = idx === listIndex;
                    return (
                      <tr
                        key={item.id || idx}
                        ref={(el) => (rowRefs.current[idx] = el)}
                        onClick={() => {
                          setListIndex(idx);
                          loadItem(item);
                        }}
                        style={{
                          background: isSelected ? rowBgSelected : isCursor ? rowBgCursor : "white",
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle}>{formatDateNL(item.application_date)}</td>
                        <td style={tdStyle} title={relationName(item)}>{relationName(item)}</td>
                        <td style={tdStyle} title={item.title}>{item.title}</td>
                        <td style={tdStyle} title={item.job_title}>{item.job_title}</td>
                        <td style={tdStyle}>{item.status}</td>
                        <td style={tdStyle}>{item.cao}</td>
                        <td style={tdStyle}>{item.wage_period}</td>
                        <td style={tdStyle}>{item.invoice_period}</td>
                        <td style={tdStyle}>{decimalInput(item.factor)}</td>
                        <td style={tdStyle}>{boolToJaNee(parseActiveValue(item) !== false)}</td>
                        <td style={tdStyle} title={companyCodeName(item)}>{companyCodeName(item) || "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Aanvraag</h2>
              <span style={{ fontSize: 12, opacity: 0.55 }}>{mode === "form" ? (form.id ? `#${form.id}` : "nieuw") : "geen selectie"}</span>
            </div>

            {saveError && <div style={{ marginBottom: 10, color: "#b00020", fontSize: 13 }}>{saveError}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <div style={sectionTitleStyle}>Algemeen</div>
              {renderTextField("Aanvraagdatum", "application_date", dateRef, "date")}
              {renderTextField("Aanvraagnummer", "application_number", numberRef)}

              <label style={labelStyle}>Relatie</label>
              <select
                ref={relationRef}
                value={form.organisation_id || ""}
                disabled={isFormLocked}
                onChange={(e) => {
                  const organisationId = e.target.value;
                  setForm({
                    ...form,
                    organisation_id: organisationId,
                    contact_id: "",
                    company_code_id: orgCompanyCodeIdFromList(organisations, organisationId),
                    company_code_name: orgCompanyCodeNameFromList(organisations, organisationId),
                  });
                }}
                style={selectStyle}
              >
                <option value="">-</option>
                {organisations.map((o) => {
                  const id = orgIdOf(o);
                  const name = pickOrgName(o);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Contactpersoon</label>
              <select
                ref={contactRef}
                value={form.contact_id || ""}
                disabled={isFormLocked || !form.organisation_id}
                onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                style={selectStyle}
              >
                <option value="">-</option>
                {contacts.map((c) => {
                  const id = contactIdOf(c);
                  const name = pickContactName(c);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Bedrijfscode</label>
              <input
                type="text"
                value={applicationCompanyCodeName(form, organisations)}
                disabled
                readOnly
                style={{ ...inputStyle, background: "#f7f7f7" }}
              />


              {renderTextField("Functie / vacaturetitel", "job_title", functionRef)}
              {renderTextField("Aanvraag", "title", titleRef)}
              {renderTextField("Aantal personen", "number_of_people", peopleRef, "number")}
              {renderSelectField("Status", "status", statusRef, ["Concept", "Open", "In behandeling", "Kandidaten voorgesteld", "Gedeeltelijk ingevuld", "Ingevuld", "On hold", "Vervallen", "Geannuleerd"])}
              {renderTextareaField("Omschrijving werkzaamheden", "work_description", workDescriptionRef, 80)}

              <div style={sectionTitleStyle}>Periode / locatie</div>
              {renderTextField("Startdatum", "start_date", startDateRef, "date")}
              {renderTextField("Einddatum", "end_date", endDateRef, "date")}
              {renderDecimalField("Uren per week", "hours_per_week", hoursRef, 2)}
              {renderTextareaField("Werktijden / roostertekst", "schedule_text", scheduleRef, 70)}
              {renderTextField("Locatie", "location", locationRef)}
              {renderTextField("Plaats", "place", placeRef)}
              {renderTextField("Regio", "region", regionRef)}

              <div style={sectionTitleStyle}>Eisen / profiel</div>
              {renderTextField("Opleiding", "education", educationRef)}
              {renderTextField("Werkervaring", "experience", experienceRef)}
              {renderTextField("Certificaten", "certificates", certificatesRef)}
              {renderTextField("Rijbewijs", "driving_license", drivingLicenseRef)}
              {renderSelectField("Eigen vervoer", "own_transport", ownTransportRef, ["Ja", "Nee", "Gewenst", "Niet nodig"])}
              {renderTextField("Talen", "languages", languagesRef)}
              {renderTextareaField("Matchingtekst / profiel", "matching_text", matchingTextRef, 90)}

              <div style={sectionTitleStyle}>Tarieven / afspraken</div>
              {renderTextField("CAO", "cao", caoRef)}
              {renderSelectField("Loonperiode", "wage_period", wagePeriodRef, ["week", "4-weken", "maand"])}
              {renderSelectField("Factuurperiode", "invoice_period", invoicePeriodRef, ["week", "4-weken", "maand"])}
              {renderDecimalField("Factor", "factor", factorRef, 2, { autoCalculateSalesRate: true })}
              {renderMoneyField("Salarisindicatie", "rate_indication", rateIndicationRef, 2, { autoCalculateSalesRate: true })}
              {renderMoneyField("Verkooptarief", "sales_rate", salesRateRef)}
              {renderMoneyField("Minimaal tarief", "min_rate", minRateRef)}
              {renderMoneyField("Reiskosten", "travel_costs", travelCostsRef, 2)}
              {renderTextareaField("Toeslagen", "allowances", allowancesRef, 60)}
              {renderTextareaField("Factuurafspraak", "invoice_agreement", invoiceAgreementRef, 70)}

              <div style={sectionTitleStyle}>Proces</div>
              {renderSelectField("Prioriteit", "priority", priorityRef, ["Laag", "Normaal", "Hoog", "Spoed"])}
              {renderTextField("Bron aanvraag", "request_source", sourceRef)}
              {renderTextField("Deadline voorstellen", "proposal_deadline", proposalDeadlineRef, "date")}

              <div style={sectionTitleStyle}>Opmerkingen</div>
              {renderTextareaField("Opmerking intern", "internal_note", internalNoteRef, 80)}
              {renderTextareaField("Opmerking extern", "external_note", externalNoteRef, 80)}
              {renderCheckbox("Actief", "active")}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 8,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <button
                  onClick={openMatchingPersoneelFromApplication}
                  disabled={!form.id && !selectedId}
                  style={{
                    ...(!form.id && !selectedId
                      ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#198754")),
                    gridColumn: "1 / -1",
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🧩 Match personeel
                </button>

                <button
                  onClick={saveItem}
                  disabled={isReadonlyRole || mode !== "form"}
                  style={{
                    ...(isReadonlyRole || mode !== "form"
                      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#2f7dd1")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  💾 Opslaan
                </button>

                <button
                  onClick={deleteItem}
                  disabled={isReadonlyRole || !form.id}
                  style={{
                    ...(isReadonlyRole || !form.id
                      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#d64545")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🗑 Verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
